import { type RefObject, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { Item, Note, Research, TripView } from "./api.ts";
import { ago, kindClass, kindOf } from "./format.ts";
import { asLabel } from "./former.tsx";
import { useKept } from "./kept.ts";
import { AddNote, type NoteEditing, NoteList } from "./Notes.tsx";
import { litProps } from "./opened.ts";
import { RichText } from "./RichText.tsx";
import { useReadOnly } from "./readOnly.ts";
import { researchFoot, sourceText } from "./research.ts";
import { KindIcon, SectionTitle } from "./ui.tsx";

// Research as it's shown (features/research.md, "A tile of one height, opened to be read in full"): a tile
// of one height wherever it's listed (Ideas, the card of what it's about), opening the finding to be read in
// full over the view it's in (TripPage: `?view=ideas&research=<id>`), on a computer as a modal (Modal.tsx).

// A finding with what it's about (the items, in the trip) and people's notes on it.
export interface Finding {
  research: Research;
  about: Item[];
  notes: Note[];
}

// Who found it, through which AI, and when; and who added to it since, when someone else did.
function byline(r: Research, trip: TripView): string {
  const who = (by: { id: string; name: string }) => (by.id === trip.you ? "You" : asLabel(by.name));
  const via = r.created.via === "web" ? " on the web" : r.created.via ? ` via ${r.created.via}` : "";
  const added = r.updated.at !== r.created.at && r.updated.by.id !== r.created.by.id;
  return `${who(r.created.by)}${via} · ${ago(r.created.at)}${
    added ? ` · added to by ${who(r.updated.by).replace(/^You$/, "you")} ${ago(r.updated.at)}` : ""
  }`;
}

// Whether what's in `ref` runs past its end (so it fades out there), again as its size changes. Research's
// tiles and options' (Options.tsx).
export function useCut(ref: RefObject<HTMLElement | null>): boolean {
  const [cut, setCut] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setCut(el.scrollHeight > el.clientHeight + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [ref]);
  return cut;
}

// The tile: the question, who found it and when, the answer's start fading out where the tile ends, and a
// foot that's never cut ("13 sources · 16 places · 1 note"). Every tile is the same height, whatever its
// answer's length (app.css). The whole tile is one control, its question: it opens the finding to be read.
// Lit when it's the last thing opened from where it's listed.
export function ResearchTile({
  finding,
  trip,
  lit = false,
  onRead,
}: {
  finding: Finding;
  trip: TripView;
  lit?: boolean;
  onRead: (id: string) => void;
}) {
  const { research: r, about, notes } = finding;
  const id = useId();
  const answer = useRef<HTMLDivElement>(null);
  const cut = useCut(answer);
  const foot = researchFoot(r.sources.length, about, notes.length);
  return (
    <article className={`tm-research${lit ? " is-lit" : ""}`} aria-labelledby={`${id}-q`}>
      <h3 className="body-strong tm-research-title">
        <button
          id={`${id}-q`}
          type="button"
          className="tm-research-open"
          {...(foot && { "aria-describedby": `${id}-foot` })}
          {...litProps(lit)}
          onClick={() => onRead(r.id)}
        >
          {r.title}
        </button>
      </h3>
      <p className="caption tm-muted tm-research-meta">{byline(r, trip)}</p>
      <div className={`tm-research-answer${cut ? " is-cut" : ""}`} ref={answer}>
        <RichText text={r.text} className="callout" links={false} />
      </div>
      {foot && (
        <p id={`${id}-foot`} className="caption tm-muted tm-research-foot">
          {foot}
        </p>
      )}
    </article>
  );
}

// The finding in full, to be read: the question, who found it and when, the whole answer, every source,
// what it's about (each opening its card over it) and the notes, with Add a note. Its title takes the focus
// as it opens. What's around it (the page over the view on a computer, the sheet on a phone, and its close)
// is TripPage's.
export function ResearchReader({
  finding,
  trip,
  edit,
  wide,
  onOpen,
}: {
  finding: Finding;
  trip: TripView;
  edit: NoteEditing;
  wide: boolean;
  onOpen: (id: string) => void;
}) {
  const { research: r, about, notes } = finding;
  // A read-only trip: its notes only when there are some (nothing can be added).
  const readOnly = useReadOnly();
  const id = useId();
  const title = useRef<HTMLHeadingElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: again for each finding opened in it
  useEffect(() => {
    title.current?.focus({ preventScroll: true });
  }, [r.id]);
  // The last of what it's about opened from here, lit, as Back finds it (kept with the step).
  const [last, setLast] = useKept<string | null>(`reader-${r.id}-lit`, null);
  return (
    <article className="tm-reader-text" aria-labelledby={`${id}-title`}>
      <header className="tm-reader-head">
        <span className="caption tm-muted">Research</span>
        <h1 id={`${id}-title`} className={wide ? "display-l" : "display-m"} ref={title} tabIndex={-1}>
          {r.title}
        </h1>
        <p className="caption tm-muted tm-reader-meta">{byline(r, trip)}</p>
      </header>
      <RichText text={r.text} className="body tm-reader-answer" />
      {r.sources.length > 0 && (
        <section className="tm-section" aria-labelledby={`${id}-sources`}>
          <SectionTitle id={`${id}-sources`} count={r.sources.length}>
            Sources
          </SectionTitle>
          <ul className="tm-reader-sources">
            {r.sources.map((url) => (
              <li key={url} className="callout">
                <a className="tm-rich-link" href={url} target="_blank" rel="noreferrer noopener">
                  {sourceText(url)}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
      {about.length > 0 && (
        <section className="tm-section" aria-labelledby={`${id}-about`}>
          <SectionTitle id={`${id}-about`} count={about.length}>
            About
          </SectionTitle>
          <ul className="tm-research-about" aria-label="About">
            {about.map((i) => (
              <li key={i.id}>
                <button
                  type="button"
                  className={`tm-near${i.id === last ? ` is-lit ${kindClass(kindOf(i))}` : ""}`}
                  {...litProps(i.id === last)}
                  onClick={() => {
                    setLast(i.id);
                    onOpen(i.id);
                  }}
                >
                  <KindIcon item={i} size={16} className="tm-near-icon" />
                  <span className="callout">{i.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {(!readOnly || notes.length > 0) && (
        <section className="tm-section" aria-labelledby={`${id}-notes`}>
          <SectionTitle id={`${id}-notes`} count={notes.length}>
            Notes
          </SectionTitle>
          {notes.length > 0 && <NoteList notes={notes} members={trip.members} you={trip.you} edit={edit} />}
          <AddNote on={r.id} edit={edit} />
        </section>
      )}
    </article>
  );
}
